import { FP, LaunchDarkly, Rx, RxOperators } from "../deps"
import * as Environment from "../environment"
import * as Events from "../events/events"
import * as ErrorsService from "../service-errors/service-errors"
import * as ServiceIdentity from "../service-identity/service-identity"

export type Flag<
  FlagName extends string,
  VariationType = boolean
> = FlagName & { _variationType?: VariationType }

export type GlobalEnabledFlag = Flag<"global.enabled">

export type Flags = GlobalEnabledFlag

export type User = LaunchDarkly.LDUser

const trackingEvents = [
  "sellalongCtaIntent",
  "checkoutIntent",
  "panelImpressionAction",
  "purchaseStateTransitionAction",
] as ReadonlyArray<Events.EventKind>

const apiKeyE = FP.function.pipe(
  Environment.launchdarklyKey,
  FP.either.fromNullable(new Error("Launchdarkly key missing"))
)

const defaultUser: User = { anonymous: true }

const initClient = FP.function.pipe(
  defaultUser,
  FP.either.of,
  FP.either.bindTo("user"),
  FP.either.bind("apiKey", FP.function.constant(apiKeyE)),

  FP.either.traverse(FP.io.Applicative)(
    ({ apiKey, user }) =>
      () =>
        LaunchDarkly.initialize(apiKey, user)
  ),

  FP.taskEither.fromIOEither,
  FP.taskEither.chainFirst(
    FP.taskEither.tryCatchK(
      (client) => client.waitForInitialization(),
      (error) =>
        new Error(
          `Failed to initialise LaunchDarkly: ${
            error instanceof Error ? error.message : JSON.stringify(error)
          }`
        )
    )
  )
)

const client = FP.function.pipe(
  initClient,
  Rx.defer,
  RxOperators.tap(
    (clientE) =>
      FP.either.isLeft(clientE) && ErrorsService.recordError(clientE.left)
  ),
  RxOperators.filter(FP.either.isRight),
  RxOperators.map(({ right }) => right),
  RxOperators.shareReplay(1)
)

type UserAttributes = {
  hasSessionEmail: boolean
}

export type UserParameters = {
  shopifyShopId: string
  isInviteLinkReferral: boolean
  hasSeen: boolean
  identity: FP.option.Option<
    {
      customerId: string
    } & UserAttributes
  >
  viewerLocation: string
}

const toLDUser = ({
  shopifyShopId,
  hasSeen,
  isInviteLinkReferral,
  identity,
  viewerLocation,
}: UserParameters): User =>
  FP.function.pipe(
    identity,
    FP.option.fold(
      (): User => ({
        anonymous: true,
        key: ServiceIdentity.getAnonId(),
        custom: {
          shopifyShopId,
          hasSeen,
          isInviteLinkReferral,
          viewerLocation,
        },
      }),
      ({ customerId, ...rest }): User => ({
        anonymous: false,
        key: customerId,
        custom: {
          ...rest,
          shopifyShopId,
          hasSeen,
          isInviteLinkReferral,
          viewerLocation,

          // legacy
          invitedByEmail: rest.hasSessionEmail,
        },
      })
    )
  )

const getClient: FP.task.Task<LaunchDarkly.LDClient> = () =>
  Rx.firstValueFrom(client)

export const identifyUser: (
  input: UserParameters
) => FP.taskEither.TaskEither<Error, unknown> = (input) =>
  FP.function.pipe(
    getClient,
    FP.task.chain(
      FP.taskEither.tryCatchK(
        (client) => client.identify(toLDUser(input)),
        (error) =>
          new Error(
            `Failed to initialise LaunchDarkly: ${
              error instanceof Error ? error.message : JSON.stringify(error)
            }`
          )
      )
    )
  )

export const variation: <
  K extends Flags,
  V extends NonNullable<K["_variationType"]>
>(
  key: K,
  defaultValue: V
) => FP.task.Task<V> = (key, defaultValue) =>
  FP.function.pipe(
    getClient,
    FP.task.map((client): typeof defaultValue =>
      client.variation(key, defaultValue)
    )
  )

const trackEvents = FP.function.pipe(
  Events.eventBus,
  RxOperators.filter(({ kind }) =>
    FP.function.pipe(trackingEvents, FP.readonlyArray.elem(FP.string.Eq)(kind))
  )
)

export const initEventTracking = () =>
  FP.function
    .pipe(
      Rx.combineLatest([client, trackEvents]),
      RxOperators.tap(([client, event]) => client.track(event.kind))
    )
    .subscribe()
